"use client";

/**
 * CollabV3Participants 컴포넌트
 * @description 제목 아래 참여자 줄 — 겹친 얼굴 셋과 `Hyunjin, Noor and 41 more joined`. 앞 두 명 이름은
 *   진하게. 0명이면 얼굴 없이 안내만 한다(앱 `CollabParticipantsRow`).
 *
 *   상세 응답의 얼굴(`participantsPreview`)과 수만 쓴다. 웹에는 참여자 목록이 없다 — 누르면 앱의 이
 *   콜랩을 연다(앱에서는 목록 시트가 열리는 자리다). 0명이면 누를 것이 없다.
 * @example <CollabV3Participants participants={detail.participants} totalCount={detail.participantsRowCount} subject={subject} />
 */
import { memo, useMemo } from "react";

import CollabV3AvatarStack from "./CollabV3AvatarStack";
import { collabV3DetailStyles } from "./CollabV3Detail.styles";
import type { CollabV3ParticipantsProps } from "./CollabV3Detail.types";
import { useCollabV3OpenApp } from "./useCollabV3OpenApp";

import { COLLAB_V3_COPY } from "../../constants/collabV3Copy";
import { collabV3AppPaths } from "../../utils/collabV3AppPaths";

const NAME_COUNT = 2;
const NAME_SEPARATOR = ", ";

const CollabV3Participants = memo<CollabV3ParticipantsProps>(
  ({ participants, totalCount, subject }) => {
    const styles = collabV3DetailStyles.participants;
    const copy = COLLAB_V3_COPY.participants;
    const path = useMemo(
      () => collabV3AppPaths(subject.collabId).detail,
      [subject.collabId],
    );
    const open = useCollabV3OpenApp(subject, "participants", path);
    const names = useMemo(
      () =>
        participants
          .slice(0, NAME_COUNT)
          .map((person) => person.name)
          .join(NAME_SEPARATOR),
      [participants],
    );
    const namedCount = Math.min(participants.length, NAME_COUNT);
    const more = Math.max(totalCount - namedCount, 0);

    if (totalCount === 0 || participants.length === 0) {
      return <p className={styles.text}>{copy.empty}</p>;
    }
    return (
      <button type="button" onClick={open} className={styles.root}>
        <CollabV3AvatarStack people={participants} />
        <span className={styles.text}>
          <span className={styles.names}>{names}</span>
          {more > 0 ? copy.more(more) : copy.only}
        </span>
      </button>
    );
  },
);

CollabV3Participants.displayName = "CollabV3Participants";

export default CollabV3Participants;
